/**
 * React hook to handle the dark/light mode of the theme
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { useGetCurrentUserQuery } from '../../../services/users';
import { ThemeMode } from '../../../theme';
import { CacheKey } from '../../common/constants';

import useUserId from './useUserId';

const useDarkMode = (isDarkMode: boolean) => {
  const userId = useUserId();
  const [darkMode, setDarkMode] = useState<boolean>(isDarkMode);

  const { data: currUser } = useGetCurrentUserQuery();

  const initDarkMode = useCallback(() => {
    setDarkMode(true);
    localStorage.setItem(CacheKey.ThemeMode, ThemeMode.Dark);
  }, []);

  const initLightMode = useCallback(() => {
    setDarkMode(false);
    localStorage.setItem(CacheKey.ThemeMode, ThemeMode.Light);
  }, []);

  // Note: not a hook — deliberately avoids the `use` prefix so hook lint rules
  // don't treat its conditional calls inside the effect below as violations
  const applyFallbackThemeMode = useCallback(() => {
    setDarkMode(isDarkMode);
  }, [isDarkMode]);

  // Sync from the server's themeMode ONCE per session — when both userId and
  // currUser become known. After that, local state is the source of truth so
  // optimistic toggles in SettingsAccountPage don't get clobbered by a stale
  // currUser refetch during an in-flight updateUser mutation (which would
  // briefly revert the theme to the last server-confirmed value before the new
  // value lands — visible as a Dark→Light→Dark flicker).
  const syncedFromServer = useRef(false);

  useEffect(() => {
    if (syncedFromServer.current) {
      return;
    }
    if (!userId) {
      applyFallbackThemeMode();
      return;
    }
    if (!currUser?.data) {
      return;
    } // wait for first load before syncing
    const serverMode = currUser.data.settings?.themeMode;
    if (serverMode === ThemeMode.Dark) {
      initDarkMode();
    } else if (serverMode === ThemeMode.Light) {
      initLightMode();
    } else {
      applyFallbackThemeMode();
    }
    syncedFromServer.current = true;
  }, [currUser, userId, initDarkMode, initLightMode, applyFallbackThemeMode]);

  return {
    darkMode,
    initLightMode,
    initDarkMode,
  };
};

export default useDarkMode;
